import { all, call, put, takeEvery } from 'redux-saga/effects';
import Api from 'src/helpers/api';
import { sendRequest } from 'src/helpers/saga';
import {
  IMutationResult,
  PRESERVE_STATUS,
  firstValidationMessage,
  isMutationSuccess,
  mutationErrorNotification,
  mutationSuccessNotification,
} from 'src/helpers/mutations';
import { logger } from 'src/helpers/logger';
import notifications from 'src/redux/notifications/actions';
import { t } from 'src/i18n';
import { JOB_TYPES_ENDPOINT } from '../constants';
import actions from './actions';
import type { ICreateRequestPayload, IDeleteRequestPayload, IRenameRequestPayload, IValidations } from './types';

type MutationAction<P> = { type: string; payload: P };

type MutationSaga = Generator<unknown, void, IMutationResult>;

const api = new Api(`${JOB_TYPES_ENDPOINT}/:id`);

function successNotification(messageKey: string) {
  return mutationSuccessNotification(messageKey, 'dash.admin.jobTypes.entity_label');
}

function genericErrorNotification() {
  return mutationErrorNotification(t('dash.common.notification.something_went_wrong'));
}

// An empty map counts as none, or the toast is suppressed and the dialog has nothing to show either.
function validationsOf(result: IMutationResult): IValidations | null {
  const validations = result?.status === 422 ? result.validations : undefined;

  return validations && firstValidationMessage(validations) ? validations : null;
}

export function* createRequest({ payload }: MutationAction<ICreateRequestPayload>): MutationSaga {
  try {
    const result = yield call(
      sendRequest,
      api.createItem,
      { data: { job_type: { name: payload.name } } },
      notifications.REQUEST_ERROR,
      undefined,
      undefined,
      PRESERVE_STATUS,
    );

    if (isMutationSuccess(result)) {
      yield put(actions.createSuccess());
      yield put(successNotification('dash.common.notification.created_successfully'));

      return;
    }

    const validations = validationsOf(result);
    yield put(actions.createError({ validations }));

    if (result && !validations) {
      yield put(genericErrorNotification());
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.createError({ validations: null }));
    yield put(genericErrorNotification());
  }
}

export function* renameRequest({ payload }: MutationAction<IRenameRequestPayload>): MutationSaga {
  try {
    const result = yield call(
      sendRequest,
      api.patchItem,
      { id: payload.id, data: { job_type: { name: payload.name } } },
      notifications.REQUEST_ERROR,
      undefined,
      undefined,
      PRESERVE_STATUS,
    );

    if (isMutationSuccess(result)) {
      yield put(actions.renameSuccess());
      yield put(successNotification('dash.common.notification.updated_successfully'));

      return;
    }

    const validations = validationsOf(result);
    yield put(actions.renameError({ validations }));

    if (result && !validations) {
      yield put(genericErrorNotification());
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.renameError({ validations: null }));
    yield put(genericErrorNotification());
  }
}

export function* deleteRequest({ payload }: MutationAction<IDeleteRequestPayload>): MutationSaga {
  try {
    const result = yield call(
      sendRequest,
      api.deleteItem,
      { id: payload.id },
      notifications.REQUEST_ERROR,
      undefined,
      undefined,
      PRESERVE_STATUS,
    );

    if (isMutationSuccess(result)) {
      yield put(actions.deleteSuccess());
      yield put(successNotification('dash.common.notification.deleted_successfully'));

      return;
    }

    yield put(actions.deleteError());

    if (result) {
      yield put(genericErrorNotification());
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.deleteError());
    yield put(genericErrorNotification());
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.CREATE_REQUEST, createRequest),
    takeEvery(actions.RENAME_REQUEST, renameRequest),
    takeEvery(actions.DELETE_REQUEST, deleteRequest),
  ]);
}
